import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import { OffersInfo } from './types';
import { mapListeningOptions } from './map-listening-options';
import { mapIcons } from './map-icons';
import { mapDealRedemptionCtaLinkInfo } from './map-deal-redemption-cta-link-info';
import { OffersCommerceInfoResponse } from '@sxm-de/dev-kits/microservices-sdk/types';

export const mapOffersInfoByLocale = (
    offerInfosData: OffersCommerceInfoResponse,
): any[] => {
    const imagesBaseUrl = 'https://www.siriusxm.com';
    const offersInfo: OffersInfo[] = [];
    const locales = offerInfosData.locales;
    Object.keys(offerInfosData.locales).forEach((locale: string) => {
        const offerInfos = offerInfosData.locales[locale];
        offerInfos.map((info: any) => {
            const offerDescription = info.offerDescription || null;
            const listeningOptions = mapListeningOptions(
                offerDescription?.listeningOptions,
            );
            const icons = mapIcons(offerDescription?.listeningOptions, locale);
            offersInfo.push({
                locale,
                planCode: info.planCode,
                salesHero: {
                    ...info.salesHero,
                    presentation: info.salesHero?.presentation,
                    subTitle: convertIfContentfulDocument(
                        info.salesHero?.subTitle,
                    ),
                    title: convertIfContentfulDocument(info.salesHero?.title),
                },
                paymentInterstitialBullets:
                    info.paymentInterstitialBullets?.map(
                        convertIfContentfulDocument,
                    ),
                offerDescription: {
                    ...offerDescription,
                    packageName: convertIfContentfulDocument(
                        offerDescription?.packageName,
                    ),
                    highlightsTitle: convertIfContentfulDocument(
                        offerDescription?.highlightsTitle,
                    ),
                    highlightsText: offerDescription?.highlightsText?.map(
                        convertIfContentfulDocument,
                    ),
                    listeningOptions,
                    longDescription: convertIfContentfulDocument(
                        offerDescription?.longDescription,
                    ),
                    priceAndTermDescTitle: convertIfContentfulDocument(
                        offerDescription?.priceAndTermDescTitle,
                    ),
                    processingFeeDisclaimer: convertIfContentfulDocument(
                        offerDescription?.processingFeeDisclaimer,
                    ),
                    recapDescription: convertIfContentfulDocument(
                        offerDescription?.recapDescription,
                    ),
                    recapLongDescription: convertIfContentfulDocument(
                        offerDescription?.recapLongDescription,
                    ),
                    icons,
                    footer: convertIfContentfulDocument(
                        offerDescription?.footer,
                    ),
                },
                offerDetails: (info.offerDetails
                    ? updateAnchorTagsWithTarget(
                          convertIfContentfulDocument(info.offerDetails),
                          '_blank',
                      )
                    : null) as unknown as string,
                renewalOfferDetails: convertIfContentfulDocument(
                    info.renewalOfferDetails,
                ),
                deals: info.deals?.map((deal: any) => ({
                    ...deal,
                    description: convertIfContentfulDocument(deal.description),
                    title: convertIfContentfulDocument(deal.title),
                    deviceImage: deal.deviceImage
                        ? deal.deviceImage.startsWith('http')
                            ? deal.deviceImage
                            : `${imagesBaseUrl}${deal.deviceImage}`
                        : null,
                    productImage: deal.productImage
                        ? deal.productImage.startsWith('http')
                            ? deal.productImage
                            : `${imagesBaseUrl}${deal.productImage}`
                        : null,
                    dealRedemptionCTAInfo: {
                        ...deal.dealRedemptionCTAInfo,
                        description: convertIfContentfulDocument(
                            deal.dealRedemptionCTAInfo.description,
                        ),
                        image: deal.dealRedemptionCTAInfo.image
                            ? deal.dealRedemptionCTAInfo.image.startsWith(
                                  'http',
                              )
                                ? deal.dealRedemptionCTAInfo.image
                                : `${imagesBaseUrl}${deal.dealRedemptionCTAInfo.image}`
                            : null,
                        cTALinkInfo: mapDealRedemptionCtaLinkInfo(
                            deal.dealRedemptionCTAInfo.cTALinkInfo,
                        ),
                        mobileCTALinkInfo: mapDealRedemptionCtaLinkInfo(
                            deal.dealRedemptionCTAInfo.mobileCTALinkInfo,
                        ),
                    },
                })),
                packageDescription: {
                    packageName: convertIfContentfulDocument(
                        offerDescription?.packageName,
                    ),
                    highlightsTitle: convertIfContentfulDocument(
                        offerDescription?.highlightsTitle,
                    ),
                    highlightsText: offerDescription?.highlightsText?.map(
                        convertIfContentfulDocument,
                    ),
                    listeningOptions,
                    longDescription: convertIfContentfulDocument(
                        offerDescription?.longDescription,
                    ),
                    icons,
                    footer: convertIfContentfulDocument(
                        offerDescription?.footer,
                    ),
                    packageFeatures: offerDescription?.packageFeatures?.map(
                        (packageFeature: any) => ({
                            ...packageFeature,
                            features: packageFeature.features?.map(
                                (feature: any) => ({
                                    ...feature,
                                    shortDescription:
                                        convertIfContentfulDocument(
                                            feature?.shortDescription,
                                        ),
                                    toolTipText: feature?.tooltipText,
                                    name: feature?.name,
                                    learnMoreLinkText:
                                        feature?.learnMoreLinkText,
                                    learnMoreInformation:
                                        convertIfContentfulDocument(
                                            feature?.learnMoreInformation,
                                        ),
                                }),
                            ),
                        }),
                    ),
                    packageHideToggleText: offerDescription?.hideToggleLabel,
                    packageShowToggleText: offerDescription?.showToggleLabel,
                },
                addonHeaderOverride: info.addonHeaderOverride,
                presentation: info.presentation,
                numberOfBullets: info.numberOfBullets,
                offerDisplayName: info.offerDisplayName,
            });
        });
    });

    return offersInfo;
};

const decodeHtmlCharacters = (html: string) => {
    const txt = document.createElement('textarea');
    txt.innerHTML = html;
    return txt.value;
};

const convertIfContentfulDocument = (value: string) => {
    if (value?.startsWith('{')) {
        const parsed = documentToHtmlString(JSON.parse(value), {
            preserveWhitespace: true,
        });
        return decodeHtmlCharacters(parsed);
    } else {
        return value;
    }
};

const updateAnchorTagsWithTarget = (
    html: string,
    targetValue: string,
): string => {
    const regex = /<a\s+(?!.*?\btarget=)[^>]*>/g;
    return html.replace(regex, (match) => {
        return match.slice(0, -1) + ` target="${targetValue}">`;
    });
};
